import * as cookie from 'cookie'
import type { ContentfulEducationalComponent } from '@reward-platform/contentful'
import { ONE_HOUR } from '~/utils/datetime'
import { isClient } from '~/utils/envChecks'

// Contentful componentEducational entry, tagged per opCo
export const SIGN_IN_BENEFITS_CONTENTFUL_IDENTIFIER = 'rp-sign-in-benefits-modal'

// A delay is only useful up to a point, and a mistyped Kameleoon variable should not
// leave the modal waiting for hours
const MAX_OPEN_DELAY_SECONDS = 120

/**
 * How long to wait before showing the modal, from the Kameleoon delay-seconds variable.
 * Anything missing or not a usable number shows the modal as soon as the page has loaded.
 */
export const getOpenDelayMs = (delaySeconds?: number): number => {
  if (typeof delaySeconds !== 'number' || !Number.isFinite(delaySeconds) || delaySeconds <= 0) {
    return 0
  }

  return Math.min(delaySeconds, MAX_OPEN_DELAY_SECONDS) * 1000
}

const DEFAULT_SHOW_AGAIN_AFTER_HOURS = 24

/**
 * How long after it was shown the modal may be shown again, from the Kameleoon
 * show-again-after-hours variable. Anything missing or not a usable number uses the default.
 */
export const getShowAgainAfterMs = (showAgainAfterHours?: number): number => {
  if (
    typeof showAgainAfterHours !== 'number' ||
    !Number.isFinite(showAgainAfterHours) ||
    showAgainAfterHours <= 0
  ) {
    return DEFAULT_SHOW_AGAIN_AFTER_HOURS * ONE_HOUR
  }

  return showAgainAfterHours * ONE_HOUR
}

// A cookie rather than sessionStorage, which is kept per tab: every tab shares the cookie, so the
// modal is not shown again in the new tab a details page opens in. It holds when the modal was shown
export const SIGN_IN_BENEFITS_MODAL_SEEN_COOKIE = 'rp_sign_in_benefits_modal_seen'

export const hasSeenSignInBenefitsModal = (showAgainAfterMs: number, now = Date.now()): boolean => {
  if (isClient()) {
    // Nothing could remember it, so it would open on every page. Signing in needs cookies anyway
    if (!navigator.cookieEnabled) {
      return true
    }

    const shownAt = Number(cookie.parse(document.cookie)[SIGN_IN_BENEFITS_MODAL_SEEN_COOKIE])
    // Up to an hour ahead is the rounding in markSignInBenefitsModalSeen, anything later was not
    // written by it
    return Number.isFinite(shownAt) && shownAt - now <= ONE_HOUR && now - shownAt < showAgainAfterMs
  }

  return false
}

// Without an expiry it is a session cookie, so it also goes when the browser session ends.
// The time is rounded up to the hour, so the cookie sent with every request is not a near unique
// value, and it is never shown again before the show again time
export const markSignInBenefitsModalSeen = (now = Date.now()) => {
  if (isClient()) {
    const shownAt = Math.ceil(now / ONE_HOUR) * ONE_HOUR
    document.cookie = cookie.serialize(SIGN_IN_BENEFITS_MODAL_SEEN_COOKIE, String(shownAt), {
      path: '/',
      sameSite: 'lax',
      // Local development and the tests run on http, where a Secure cookie is dropped
      secure: window.location.protocol === 'https:',
    })
  }
}

export type SignInBenefit = {
  title: string
  description?: string
  iconUrl?: string
}

type EducationalItem = NonNullable<ContentfulEducationalComponent['items']>[number]

// The Contentful text field is single line, so the benefit title and description are separated by a pipe,
// e.g. "Faster checkout | Save time when proceeding to checkout"
const TITLE_DESCRIPTION_SEPARATOR = '|'

export const mapEducationalItemToBenefit = ({
  text,
  icon,
}: EducationalItem): SignInBenefit | null => {
  const [rawTitle = '', ...descriptionParts] = (text ?? '').split(TITLE_DESCRIPTION_SEPARATOR)
  const title = rawTitle.trim()

  if (!title) {
    return null
  }

  const description = descriptionParts.join(TITLE_DESCRIPTION_SEPARATOR).trim()

  return {
    title,
    ...(description ? { description } : {}),
    ...(icon?.file?.url ? { iconUrl: icon.file.url } : {}),
  }
}
